<template>
  <el-dialog
    :visible.sync="dialogVisible"
    title="All Messages"
    width="70%"
    top="5vh"
    :before-close="handleClose"
    class="messages-dialog"
    append-to-body
  >
    <div class="messages-container">
      <!-- Left: Message List -->
      <div class="message-list-panel">
        <el-scrollbar class="list-scrollbar">
          <ul>
            <li
              v-for="msg in messages"
              :key="msg.id"
              class="list-item"
              :class="{
                'is-active': activeMessageId === msg.id,
                'is-unread': !msg.is_read && activeMessageId !== msg.id,
              }"
              @click="selectMessage(msg.id)"
            >
              <span class="dot"></span>
              <span class="title-text">{{ msg.message.title }}</span>
            </li>
          </ul>
        </el-scrollbar>
      </div>

      <!-- Right: Message Detail -->
      <div class="message-detail-panel">
        <el-scrollbar ref="detailScrollbar" class="detail-scrollbar">
          <div v-loading="loading" class="detail-content">
            <template
              v-if="
                activeMessageId &&
                activeMessageDetail &&
                activeMessageDetail.message
              "
            >
              <h2 class="detail-title">
                {{ activeMessageDetail.message.title }}
              </h2>
              <p class="detail-meta">
                {{
                  new Date(
                    activeMessageDetail.message.created_time
                  ).toLocaleString()
                }}
              </p>
              <div class="detail-body" v-html="formattedContent"></div>
              <div
                v-if="activeMessageDetail.message.image_url"
                class="detail-image"
              >
                <img
                  :src="activeMessageDetail.message.image_url"
                  alt="Message Image"
                />
              </div>
            </template>
            <el-empty
              v-else-if="!loading"
              :description="$t('news.selectMessagePrompt')"
            />
          </div>
        </el-scrollbar>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { getMessageDetail } from "@/api/user";

export default {
  name: "AllMessagesModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Array,
      required: true,
    },
    initialMessageId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      activeMessageId: null,
      activeMessageDetail: null,
      loading: false,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit("update:visible", val);
      },
    },
    formattedContent() {
      if (
        this.activeMessageDetail &&
        this.activeMessageDetail.message &&
        this.activeMessageDetail.message.content
      ) {
        return this.activeMessageDetail.message.content.replace(
          /\n/g,
          "<br />"
        );
      }
      return "";
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      if (newVal) {
        this.activeMessageId = this.initialMessageId;
        if (this.initialMessageId) {
          this.fetchMessageDetail(this.initialMessageId);
          const initialMessage = this.messages.find(
            (m) => m.id === this.initialMessageId
          );
          if (initialMessage && !initialMessage.is_read) {
            this.$emit("mark-as-read", this.initialMessageId);
          }
        } else {
          this.activeMessageId = null;
          this.activeMessageDetail = null;
        }
      }
    },
    activeMessageId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.fetchMessageDetail(newId);
        this.$nextTick(() => {
          const scrollbar = this.$refs.detailScrollbar;
          if (scrollbar && scrollbar.wrap) {
            scrollbar.wrap.scrollTop = 0;
          }
        });
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit("update:visible", false);
      document.body.style.overflow = "";
    },
    selectMessage(id) {
      const message = this.messages.find((m) => m.id === id);
      if (message && !message.is_read) {
        this.$emit("mark-as-read", id);
      }
      this.activeMessageId = id;
    },
    async fetchMessageDetail(id) {
      this.loading = true;
      this.activeMessageDetail = null;
      try {
        const res = await getMessageDetail(id);
        if (res.success && res.data) {
          this.activeMessageDetail = res.data;
        } else {
          this.$message.error(res.message);
        }
      } catch (error) {
        console.error("Failed to fetch message detail:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.messages-dialog >>> .el-dialog {
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 90vh;
  overflow: hidden;
}

.messages-dialog >>> .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding: 0 !important;
  overflow: hidden;
}

.messages-container {
  display: flex;
  height: 100%;
  background-color: #ffffff;
  padding: 15px;
  gap: 15px;
  overflow: hidden;
  box-sizing: border-box;
}

.message-list-panel,
.message-detail-panel {
  background-color: #f5f7fa;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.message-list-panel {
  width: 360px;
  flex-shrink: 0;
}

.message-detail-panel {
  flex: 1;
  min-width: 0;
}

.list-scrollbar,
.detail-scrollbar {
  flex: 1;
  min-height: 0;
}

.list-scrollbar >>> .el-scrollbar__wrap,
.detail-scrollbar >>> .el-scrollbar__wrap {
  overflow-x: hidden;
}

.list-scrollbar ul {
  list-style: none;
  padding: 8px;
  margin: 0;
}
.list-item {
  padding: 14px 20px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
  color: #606266;
  position: relative;
  margin-bottom: 4px;
}
.list-item:not(:last-child)::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 20px;
  right: 20px;
  height: 1px;
  background-color: #e4e7ed;
}
.list-item .title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.list-item:hover {
  background-color: #e9eef3;
  border-radius: 4px;
}
.list-item.is-active {
  background-color: #ecf5ff;
  border-radius: 4px;
}
.list-item.is-unread .title-text {
  font-weight: 600;
  color: #303133;
}
.list-item .dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #dcdfe6;
  margin-right: 12px;
  flex-shrink: 0;
  transition: background-color 0.2s;
}
.list-item.is-active .dot {
  background-color: #409eff;
}
.detail-content {
  padding: 25px 35px;
}
.detail-body {
  word-break: break-word;
  font-size: 15px;
  line-height: 1.8;
  color: #303133;
}
.detail-title {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}
.detail-meta {
  font-size: 13px;
  color: #909399;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e6ed;
}
.detail-image {
  margin-top: 25px;
  text-align: center;
}
.detail-image img {
  max-width: 100%;
  max-height: 450px;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 4px;
  background-color: #ffffff;
}

@media (max-width: 768px) {
  .messages-dialog >>> .el-dialog {
    width: 90vw !important;
    height: 85vh !important;
    margin: 7.5vh auto !important;
  }

  .messages-container {
    flex-direction: column;
    padding: 10px;
    gap: 10px;
    height: 100%;
  }

  .message-list-panel {
    width: 100%;
    height: 35%;
    flex-shrink: 0;
  }

  .message-detail-panel {
    width: 100%;
    height: 65%;
  }

  .detail-content {
    padding: 15px 20px;
  }
  .detail-title {
    font-size: 18px;
  }
  .detail-meta {
    font-size: 12px;
    padding-bottom: 15px;
    margin-bottom: 15px;
  }
  .detail-body {
    font-size: 14px;
  }
}
</style>
